import { checksTableUrlKey } from './compliance/constants';
import {
  checkDetailsDrawerUrlKey,
  imagePackageDetailsDrawerUrlKey,
  vulnerabilityManagementDetailsVulnerabilitiesTabsUrlKey,
  imageVulnerabilityDetailsDrawerUrlKey,
  vulnerabilityManagementDetailsTabsUrlKey,
} from './constants';
import { ImageVulnerabilitiesTabEnum } from './types';
import { ImageDetailsTab } from './vulnerability-management/image-details/types';
import { VulnerabilityManagementTabValues } from './vulnerability-management/types';

export type OrgSecurityRouteKeys =
  | 'compliance'
  | 'vulnerability-management'
  | 'attack-path'
  | 'runtime'
  | 'workloads'
  | 'settings'
  | 'node-updates';

const complianceBasePath = '/organization/security/compliance';
const vulnerabilityManagementBasePath =
  '/organization/security/vulnerability-management';
const attackPathBasePath = '/organization/security/attack-path';
const runtimeBasePath = '/organization/security/runtime';
const workloadsBasePath = '/organization/security/workloads';
const settingsBasePath = '/organization/security/settings';
const nodeUpdatesBasePath = '/organization/security/node-updates';

const orgSecurityPathMap: Record<OrgSecurityRouteKeys, string> = {
  compliance: complianceBasePath,
  'vulnerability-management': vulnerabilityManagementBasePath,
  'attack-path': attackPathBasePath,
  runtime: runtimeBasePath,
  workloads: workloadsBasePath,
  settings: settingsBasePath,
  'node-updates': nodeUpdatesBasePath,
};

const getFilterParams = (
  targetUrlKey: string,
  params: { clusterIds: string[]; namespaces: string[] }
) => {
  const urlParams = new URLSearchParams();
  if (params.clusterIds?.length || params.namespaces?.length) {
    urlParams.set(targetUrlKey, encodeURIComponent(JSON.stringify(params)));
  }
  return urlParams;
};

/**
 * Redirect to OP
 */
function resolvePath(path: string) {
  return path.replace('/organization', '');
}

export const makeOrgCompliancePath = (
  options: {
    clusterIds?: string[];
    namespaces?: string[];
    checkIdForDrawer?: string;
  } = {}
) => {
  const urlParams = getFilterParams(checksTableUrlKey, {
    clusterIds: options.clusterIds ?? [],
    namespaces: options.namespaces ?? [],
  });
  if (options.checkIdForDrawer) {
    urlParams.set(checkDetailsDrawerUrlKey, options.checkIdForDrawer);
  }
  return `${resolvePath(complianceBasePath)}?${urlParams.toString()}`;
};

export const makeOrgVulnerabilityManagementPath = (
  tab?: VulnerabilityManagementTabValues
) => {
  return `${resolvePath(vulnerabilityManagementBasePath)}${
    tab ? `/${tab}` : ''
  }`;
};

export const makeOrgVulnerabilityManagementDetailsPath = (
  id: string,
  options: {
    tab?: ImageDetailsTab;
    vulnerabilityIdForDrawer?: string;
    packageIdForDrawer?: string;
  } = {}
) => {
  const urlParams = new URLSearchParams();
  if (options?.tab) {
    urlParams.set(vulnerabilityManagementDetailsTabsUrlKey, options.tab);
  }
  if (options.vulnerabilityIdForDrawer) {
    urlParams.set(
      vulnerabilityManagementDetailsVulnerabilitiesTabsUrlKey,
      ImageVulnerabilitiesTabEnum.VULNERABILITIES
    );
    urlParams.set(
      imageVulnerabilityDetailsDrawerUrlKey,
      options.vulnerabilityIdForDrawer
    );
  }
  if (options.packageIdForDrawer) {
    urlParams.set(
      vulnerabilityManagementDetailsVulnerabilitiesTabsUrlKey,
      ImageVulnerabilitiesTabEnum.PACKAGES
    );
    urlParams.set(imagePackageDetailsDrawerUrlKey, options.packageIdForDrawer);
  }
  return `${vulnerabilityManagementBasePath}/${id}?${urlParams.toString()}`;
};

export const makeAttackPathDetailsPath = (id: string) => {
  return `${attackPathBasePath}/${id}`;
};

export const makeRuntimePath = (tab: 'anomalies' | 'rules') => {
  return `${runtimeBasePath}/${tab}`;
};

export const makeAnomalyDetailsPath = (anomalyId: string) => {
  return `${runtimeBasePath}/anomalies/${anomalyId}`;
};
export const makeWorkloadsPath = (
  id: string,
  tab?: 'compliance' | 'vulnerability-management' | 'runtime' | 'attack-path'
) => {
  return `${workloadsBasePath}/${id}${tab ? `/${tab}` : ''}`;
};

export const makeImageDetailsPath = (id: string) => {
  return `${vulnerabilityManagementBasePath}/image-security/${id}`;
};

export const getOrgSecurityRoutePath = (key: OrgSecurityRouteKeys) => {
  return resolvePath(orgSecurityPathMap[key]);
};
